/**
 * In-memory avatar URL cache — avoids N+1 Firestore reads while navigating Arena tabs.
 * Only stores real http(s) / data photos (never default SVG initials).
 * After upload/remove, rememberPlayerPhotoUrl updates this so lists refresh without reload.
 */
import * as playerRepository from "./playerRepository";
import { PROFILE_PHOTO_LOCAL_MARKER } from "../domain/identity/playerProfileConstants";
import { getLocalProfilePhotoDataUrl } from "./localProfilePhotoStorage";
import { withMediaCacheBust } from "../services/media/mediaUrl";

/** @type {Map<string, string | null>} */
const cache = new Map();

/** @type {Map<string, string>} */
const genderCache = new Map();

/**
 * @param {import('../domain/identity/playerTypes').PlayerRecord | null | undefined} player
 * @returns {string | null}
 */
export function extractPlayerPhotoUrl(player) {
  if (!player?.playerId) return null;

  if (cache.has(player.playerId)) {
    const remembered = cache.get(player.playerId);
    if (remembered == null) return null;
    return withMediaCacheBust(remembered, player.photoUpdatedAt);
  }

  const local = getLocalProfilePhotoDataUrl(player.playerId);
  if (local) return local;
  const ref = typeof player.profilePhoto === "string" ? player.profilePhoto.trim() : "";
  if (
    ref &&
    ref !== PROFILE_PHOTO_LOCAL_MARKER &&
    (ref.startsWith("http://") || ref.startsWith("https://") || ref.startsWith("data:image/"))
  ) {
    return withMediaCacheBust(ref, player.photoUpdatedAt);
  }
  return null;
}

/**
 * @param {string} playerId
 * @param {string | null} url
 */
export function rememberPlayerPhotoUrl(playerId, url) {
  if (!playerId) return;
  cache.set(playerId, url || null);
}

/**
 * @param {string[]} playerIds
 * @returns {Promise<Record<string, string | null>>}
 */
export async function resolvePlayerPhotoUrls(playerIds) {
  /** @type {string[]} */
  const unique = [...new Set((playerIds || []).filter(Boolean))];
  const missing = unique.filter((id) => !cache.has(id) || !genderCache.has(id));
  if (missing.length) {
    await Promise.all(
      missing.map(async (playerId) => {
        try {
          const player = await playerRepository.findById(playerId);
          cache.set(playerId, extractPlayerPhotoUrlFromDoc(player));
          genderCache.set(playerId, String(player?.gender || "").trim());
        } catch {
          if (!cache.has(playerId)) cache.set(playerId, null);
          if (!genderCache.has(playerId)) genderCache.set(playerId, "");
        }
      })
    );
  }
  /** @type {Record<string, string | null>} */
  const out = {};
  unique.forEach((id) => {
    out[id] = cache.has(id) ? cache.get(id) ?? null : null;
  });
  return out;
}

/**
 * Read photo from a Firestore player doc without consulting the memory cache
 * (used when hydrating the cache).
 * @param {import('../domain/identity/playerTypes').PlayerRecord | null | undefined} player
 */
function extractPlayerPhotoUrlFromDoc(player) {
  if (!player?.playerId) return null;
  const local = getLocalProfilePhotoDataUrl(player.playerId);
  if (local) return local;
  const ref = typeof player.profilePhoto === "string" ? player.profilePhoto.trim() : "";
  if (
    ref &&
    ref !== PROFILE_PHOTO_LOCAL_MARKER &&
    (ref.startsWith("http://") || ref.startsWith("https://") || ref.startsWith("data:image/"))
  ) {
    return withMediaCacheBust(ref, player.photoUpdatedAt);
  }
  return null;
}

/**
 * @param {Array<{ playerId?: string | null, avatar?: string | null }>} entries
 */
export async function enrichEntriesWithPlayerPhotos(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const need = list.filter((e) => e?.playerId).map((e) => e.playerId);
  if (!need.length) return list;
  const urls = await resolvePlayerPhotoUrls(need);
  return list.map((e) => {
    if (!e?.playerId) return e;
    const photo = !e.avatar ? urls[e.playerId] : null;
    const gender = genderCache.get(e.playerId) || "";
    if (!photo && !gender) return e;
    return {
      ...e,
      ...(photo ? { avatar: photo } : {}),
      ...(gender ? { gender } : {}),
    };
  });
}

export function clearPlayerPhotoUrlCache() {
  cache.clear();
  genderCache.clear();
}
